/**
 * Global Currency Formatting Utility
 * Provides consistent currency formatting across the application based on user preferences
 */

export type CurrencyType = string;

const DEFAULT_CURRENCY = 'USD';

export function getCurrencySymbol(currency: string = DEFAULT_CURRENCY): string {
  try {
    return Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 0 })
      .formatToParts(0).find(p => p.type === 'currency')?.value || currency;
  } catch { return currency; }
}

export function formatCurrency(amount: number | string | null | undefined, options?: {
  currency?: string; minimumFractionDigits?: number; maximumFractionDigits?: number;
}): string {
  try {
    if (amount === null || amount === undefined) return formatCurrency(0, options);
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return formatCurrency(0, options);
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: options?.currency || DEFAULT_CURRENCY,
      minimumFractionDigits: options?.minimumFractionDigits ?? 2,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    }).format(num);
  } catch { return `${getCurrencySymbol(options?.currency)}${amount}`; }
}

export function formatNumber(amount: number | string | null | undefined, options?: {
  minimumFractionDigits?: number; maximumFractionDigits?: number;
}): string {
  try {
    const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
    if (isNaN(num)) return '0';
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: options?.minimumFractionDigits ?? 2,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    }).format(num);
  } catch { return String(amount || 0); }
}
